import { useEffect, useRef } from "react";
import type { ISbStoryData } from "@storyblok/react";
import {
  Link,
  useParams,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Bell02,
  Settings01,
  XClose,
  Menu04,
  SearchLg,
  Users01,
} from "untitledui-js/react";
import { twMerge } from "tailwind-merge";
import { Button } from "@pondigitalsolutions/pon-component-library";
import { ControlCenter } from "./ControlCenter/ControlCenter";
import { NavMenu } from "@components/NavMenu";
import { Toolbar } from "@components/Toolbar";
import { Footer } from "@components/Footer";
import { NavigationMenu } from "@components/Navigation";
import { PonLogo } from "@components/PonLogo";
import { SearchContainer } from "@components/SearchContainer";
import { Notifications } from "@components/Notifications";
import { IconButton } from "@components/IconButton";
import { Loader } from "@components/Loader";
import { useOutsideClick } from "@hooks/useOutsideClick";
import { useConfigContext } from "@hooks/useConfigContext";
import { useDrawerScrollLock } from "@hooks/useDrawerScrollLock";
import { useNotificationsContext } from "@hooks/useNotificationsContext";
import { useUserContext } from "@hooks/useUserContext";
import { useMobileSearchActions } from "@hooks/useMobileSearchActions";
import { useSystemThemeListener } from "@hooks/useSystemThemeListener";
import { useParentNavigation } from "@hooks/useParentNavigation";
import { applyTheme } from "@utils/theme";
import { getSlugForLanguage } from "@utils/slugUtils";
import { isHomepage } from "@utils/homepage";
import { Theme } from "../constants";
import { useCenteredSearchWidth } from "@hooks/useCenteredSearchWidth";
import { languageDutch } from "../i18n";
import { getLanguageFromPathname } from "@utils/lang";
import { useSaMetadata } from "@hooks/useSaMetadata";
import type { SaTrackingParams } from "../types/TrackingTypes";
import { Walkthrough } from "./Walkthrough";
import { EditorPreviewBanner } from "@components/EditorPreviewBanner";
import { isStoryblokEditor } from "@utils/storyblokEditor";

// UI constants for consistent styling across toolbar buttons and z-index layering
const BUTTON_SIZE_CLASSES = "h-10 w-10 md:h-12 md:w-12";
const Z_INDEX_HIGH = "z-60";
const Z_INDEX_MEDIUM = "z-50";

interface LayoutProps {
  translatedSlug: string;
  hideWidgets?: boolean;
  hideNavigation?: boolean;
  className?: string;
  children: React.ReactNode;
  trackingParams?: SaTrackingParams;
  story?: ISbStoryData;
}

export const Layout = ({
  translatedSlug,
  hideWidgets,
  hideNavigation,
  className,
  children,
  trackingParams,
  story,
}: LayoutProps) => {
  const { i18n, t } = useTranslation();
  const { lang } = useParams();
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const { notificationsRef, settingsRef, drawerSwitchRef, state, dispatch } =
    useConfigContext();
  const { isLoading, user } = useUserContext();
  const { closeMobileSearch, openMobileSearch } = useMobileSearchActions();
  const { parent, homeHref } = useParentNavigation(story);

  // Silent state
  const initialRender = useRef(true);
  const mainContentRef = useRef<HTMLDivElement | null>(null);

  const { hasUnreadNotifications } = useNotificationsContext();
  const {
    isSettingsOpen,
    isNotificationsOpen,
    isDrawerOpen,
    isMobileSearchOpen,
  } = state;

  // When user clicks outside of the settings or widget switch, close them
  useOutsideClick(true);

  // Prevents background scroll
  useDrawerScrollLock(isDrawerOpen || isMobileSearchOpen);

  // Listen for system theme changes when user has selected "system" mode
  useSystemThemeListener(user?.themePreference);

  // Setup metadata for Simple Analytics tracking
  useSaMetadata(trackingParams);

  // Check if we're on the homepage
  const isHomepagePage = isHomepage(pathname, lang);

  // Homepage routes ("/" and "/en") are registered as literal paths with no `:lang` segment,
  // so `lang` from useParams() is always undefined there — fall back to the URL itself.
  const currentUrlLang = lang || getLanguageFromPathname(pathname);

  useEffect(() => {
    // guard - if initial render AND themePreference
    if (initialRender.current && user?.themePreference) {
      // Apply the themePreference using the server state
      applyTheme(user?.themePreference as Theme);
    }

    if (
      user?.languagePreference && // languagePreference is defined
      user?.languagePreference !== currentUrlLang && // user's languagePreference doesn't equal the language actually reflected in the URL
      isHomepagePage && // only run on homepage
      !isStoryblokEditor(searchParams) // never redirect an editor session: navigating drops the signed _storyblok_tk params and breaks the preview
    ) {
      const newPath = getSlugForLanguage(pathname, user?.languagePreference);
      // guard - if the paths do not match
      if (newPath !== pathname) {
        // Preserve the current query string (e.g. ?brand=uuid or ?category=uuid filters) across the redirect.
        void navigate({ pathname: newPath, search });
      }
    }

    const storyblokLang = searchParams.get("_storyblok_lang");
    if (storyblokLang || user?.languagePreference) {
      // Set i18n language to either Storyblok lang param for Storyblok preview or user preference.
      void i18n.changeLanguage(storyblokLang || user?.languagePreference);

      if (isHomepagePage) {
        // Set the html lang attribute based on the user preference.
        // Since the homepage is always displayed in the user preference.
        document.documentElement.lang =
          user?.languagePreference ?? languageDutch;
      } else {
        // Otherwise, set the lang attribute based on the URL.
        document.documentElement.lang = getLanguageFromPathname(pathname);
      }
    }

    // Only run on first render, on the homepage and when preferences change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isHomepagePage, user?.themePreference, user?.languagePreference]);

  // Dynamic centered search width (encapsulated)
  const { leftClusterRef, rightClusterRef, widthPx } = useCenteredSearchWidth();

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <Loader />
      </div>
    );
  }

  return (
    <>
      <Walkthrough translatedSlug={translatedSlug} />
      <EditorPreviewBanner />
      <div className="flex min-h-screen flex-col">
        <Toolbar
          className="relative mx-auto flex max-w-360 justify-between px-4 2xl:px-0"
          id="toolbar"
        >
          {/* Left cluster (logo + optional back button) */}
          <div ref={leftClusterRef} className="flex items-center gap-5">
            <Link
              to={homeHref}
              aria-label={t("Global.homeLogoAriaLabel")}
              className={twMerge(
                (isDrawerOpen || isMobileSearchOpen) && Z_INDEX_HIGH
              )}
            >
              <PonLogo className="text-white" />
            </Link>
            {parent && (
              <nav
                aria-label={parent.label}
                inert={isDrawerOpen || isMobileSearchOpen}
                className="shrink-0 max-md:hidden"
              >
                <Button
                  size="md"
                  variant="soft"
                  id="back-button"
                  startIcon="ChevronLeft"
                  className="h-6 rounded-lg py-0.5 whitespace-nowrap capitalize [&>svg]:shrink-0"
                  ariaLabel={parent.label}
                  renderProp={<Link to={parent.to} state={parent.state} />}
                >
                  {parent.label}
                </Button>
              </nav>
            )}
          </div>
          {/* Right cluster (icons) */}
          <div
            ref={rightClusterRef}
            className="flex justify-end gap-1 md:gap-2"
          >
            <SearchContainer mobileIconOnly hideOnHomepage hideMobileIcon />
            {!isHomepagePage && (
              <div className="lg:hidden">
                <IconButton
                  id="mobile-search-toggle"
                  ariaLabel={
                    isMobileSearchOpen ? "Close search" : "Open search"
                  }
                  active={isMobileSearchOpen}
                  showAlert={false}
                  className={twMerge(
                    BUTTON_SIZE_CLASSES,
                    (isDrawerOpen || isMobileSearchOpen) && Z_INDEX_HIGH
                  )}
                  onClick={() => {
                    if (isMobileSearchOpen) {
                      closeMobileSearch();
                    } else {
                      openMobileSearch();

                      // Focus the textarea after overlay shows
                      setTimeout(() => {
                        const textarea = document.getElementById(
                          "userPrompt"
                        ) as HTMLTextAreaElement;
                        if (textarea) {
                          textarea.focus();
                        }
                      }, 100);
                    }
                  }}
                  icon={isMobileSearchOpen ? <XClose /> : <SearchLg />}
                />
              </div>
            )}
            <div className="w-full md:relative" ref={notificationsRef}>
              <IconButton
                id="notification-nav-item"
                ariaLabel="Notifications"
                active={isNotificationsOpen}
                showAlert={hasUnreadNotifications}
                className={twMerge(BUTTON_SIZE_CLASSES, Z_INDEX_MEDIUM)}
                onClick={() => {
                  if (isMobileSearchOpen) {
                    closeMobileSearch();
                  }

                  dispatch({
                    type: "NOTIFICATIONS_VISIBILITY",
                    payload: { visible: !isNotificationsOpen },
                  });
                }}
                icon={<Bell02 />}
              />
              <NavMenu
                isOpen={isNotificationsOpen}
                className="top-15.5 right-auto mx-4 after:right-35.5 max-md:inset-x-0 md:top-15 md:-right-16 md:after:right-15.5"
              >
                <Notifications />
              </NavMenu>
            </div>
            {!hideWidgets && (
              <div className="relative hidden md:block">
                <IconButton
                  id="people-finder-toggle"
                  ariaLabel="Open People Finder"
                  className={twMerge(BUTTON_SIZE_CLASSES, Z_INDEX_MEDIUM)}
                  showAlert={false}
                  onClick={() => {
                    void navigate(
                      getSlugForLanguage("/people-finder", lang || "nl")
                    );
                  }}
                  icon={<Users01 />}
                />
              </div>
            )}
            <div className="relative" ref={settingsRef}>
              <IconButton
                id="setting-nav-item"
                ariaLabel="Settings"
                active={isSettingsOpen}
                showAlert={false}
                className={twMerge(BUTTON_SIZE_CLASSES, Z_INDEX_MEDIUM)}
                onClick={() => {
                  if (isMobileSearchOpen) {
                    closeMobileSearch();
                  }

                  dispatch({
                    type: "SETTINGS_VISIBILITY",
                    payload: { visible: !isSettingsOpen },
                  });
                }}
                icon={<Settings01 />}
              />
              <NavMenu
                isOpen={isSettingsOpen}
                className="top-13 after:right-6 md:top-15 after:md:right-7"
              >
                <ControlCenter translatedSlug={translatedSlug} />
              </NavMenu>
            </div>
            {!hideWidgets && (
              <div className="relative">
                <IconButton
                  id="okta-toggle"
                  ariaLabel="Open Okta"
                  className={twMerge(BUTTON_SIZE_CLASSES, Z_INDEX_MEDIUM)}
                  showAlert={false}
                  onClick={() =>
                    window.open(
                      "https://pon.okta.com",
                      "_blank",
                      "noopener,noreferrer"
                    )
                  }
                  icon={
                    <img
                      src="/okta-logo-white.png"
                      alt="Okta logo"
                      className="max-h-6 max-w-6"
                    />
                  }
                />
              </div>
            )}
            {!hideNavigation && (
              <div className="relative" ref={drawerSwitchRef}>
                <IconButton
                  id="drawer-toggle"
                  ariaLabel="Toggle drawer"
                  className={twMerge(BUTTON_SIZE_CLASSES, Z_INDEX_MEDIUM)}
                  active={isDrawerOpen}
                  showAlert={false}
                  onClick={() => {
                    if (isMobileSearchOpen) {
                      closeMobileSearch();
                    }

                    dispatch({
                      type: "DRAWER_VISIBILITY",
                      payload: { visible: !isDrawerOpen },
                    });
                  }}
                  icon={isDrawerOpen ? <XClose /> : <Menu04 />}
                />
                <NavigationMenu
                  isOpen={isDrawerOpen}
                  mainContentRef={mainContentRef}
                  onNavigate={() =>
                    dispatch({
                      type: "DRAWER_VISIBILITY",
                      payload: { visible: false },
                    })
                  }
                />
              </div>
            )}
          </div>
          {/* Centered search overlay (desktop only) */}
          <div className="pointer-events-none absolute inset-y-0 left-1/2 hidden w-full -translate-x-1/2 items-center lg:flex">
            <div
              className="pointer-events-auto mx-auto w-full max-w-[var(--search-max-w)] transition-[max-width] duration-150 ease-out lg:px-4"
              data-search-width-px={widthPx}
            >
              <SearchContainer hideOnHomepage />
            </div>
          </div>
        </Toolbar>

        <div ref={mainContentRef} className={twMerge("flex-1", className)}>
          {children}
        </div>
        <Footer />
      </div>
    </>
  );
};
